import axios from "axios";
import {settingsHelperFunctions} from "../../helpers/settingsHelper";
import Swal from "sweetalert2";
import {authHeader} from "@/helpers/authservice/auth-header";
import {requiredHeaders, requiredHeadersWithClientSession} from "@/helpers/authservice/requiredHeaders";
import {apiRequestsHelper} from "@/state/pinia/apiRequestHelper";
import {log} from "./log";
import {defineStore} from "pinia";
import {useLazyLoaderModule} from "@/state/pinia/lazyloaderManagerModule";

export const useCommonAppModule = defineStore("commonAppModule", {
    state: () => ({
        lazyLoaderModule: useLazyLoaderModule(),
        // endpoints
        settings_endpoint: "/api/settings",
        basics_endpoint: "/api/app/basics",
        drugstores_endpoint: "/api/app/drugstores",

        // Common
        allowRegistration: false,
        horizontalNavbarTitle: "",
        backendModuleName: "APP",
        workingProfile: "",
        centralInstance: false,
        licensedFeatures: [],
        //Settings
        settings: [],
        userSettingsByUserId: {},

        //Drugstores
        currentDrugstore: {},
        drugstores: [],
        drugstoresNameMap: {},
        drugstoresPagesCount: 0,
        drugstoresDataTotal: 0,
        drugstoresAvailableSearchOptions: ["name","drugstoreClientId", "nip"],

        // Drugstore Groups
        drugstore_groups_endpoint: "/api/app/drugstore-groups",
        drugstoreGroups: [],
        currentDrugstoreGroup: {},

        // Suppliers data
        suppliers_endpoint: "/api/app/suppliers",
        suppliersData: [],
        suppliersDataTotal: 0,
        suppliersDataPagesCount: 0,

        rawDrugstoresSuppliersData: [],
        rawDrugstoresSuppliersDataTotal: 0,
        rawDrugstoresSuppliersPagesCount: 0,
        rawDrugstoresSuppliersHeaders: [],
        rawDrugstoresSuppliersSearchKeys: [],

        // Products data
        products_endpoint: "/api/app/products",
        productsData: [],
        productsDataTotal: 0,
        productsDataPagesCount: 0,

        rawDrugstoresProductsData: [],
        rawDrugstoresProductsDataTotal: 0,
        rawDrugstoresProductsPagesCount: 0,
        rawDrugstoresProductsHeaders: [],
        rawDrugstoresProductsSearchKeys: [],


        // Categories data
        categories_endpoint: "/api/app/product-categories",
        categoriesData: [],
        categoriesTotal: 0,
        categoriesPagesCount: 0,


        availableModules: [],
        // app process lock api
        app_process_locks_endpoint: "/api/app-process-locks",

        // Drugstore API keys (admin)
        drugstore_api_keys_admin_endpoint: "/admin/drugstores/api-keys",
        drugstoreApiKeys: [],
        drugstoreApiKeysPagesCount: 0,
        drugstoreApiKeysDataTotal: 0,

        // App Setting Overrides
        app_setting_overrides_endpoint: "/api/settings/overrides",

    }),
    getters: {
        getWorkingProfile: (state) => state.workingProfile,
        getLicensedFeatures: (state) => state.licensedFeatures,
        isCentralInstance: (state) => state.centralInstance,
        //Common
        getAllowRegistration: (state) => state.allowRegistration,
        getBackendModuleName: (state) => state.backendModuleName,
        getHorizontalNavbarTitle: (state) => state.horizontalNavbarTitle,
        getCurrentDrugstore: (state) => state.currentDrugstore,
        getDrugstores: (state) => state.drugstores,
        getDrugstoresNameMap: (state) => state.drugstoresNameMap,
        getDrugstoresPagesCount: (state) => state.drugstoresPagesCount,
        getDrugstoresDataTotal: (state) => state.drugstoresDataTotal,
        getAppCommonSettings: (state) => state.settings,
        getUserSettingsByUserId: (state) => (userId) =>
            state.userSettingsByUserId[userId] || [],
        getDrugstoresAvailableSearchOptions: (state) =>
            state.drugstoresAvailableSearchOptions,
        getDrugstoreGroups: (state) => state.drugstoreGroups,
        getCurrentDrugstoreGroup: (state) => state.currentDrugstoreGroup,

        getAvailableModules: (state) => state.availableModules,
        //Suppliers
        getSuppliersData: (state) => state.suppliersData,
        getSuppliersDataTotal: (state) => state.suppliersDataTotal,
        getSuppliersDataPagesCount: (state) => state.suppliersDataPagesCount,
        getRawDrugstoresSuppliersData: (state) => state.rawDrugstoresSuppliersData,
        getRawDrugstoresSuppliersDataTotal: (state) => state.rawDrugstoresSuppliersDataTotal,
        getRawDrugstoresSuppliersPagesCount: (state) => state.rawDrugstoresSuppliersPagesCount,
        getRawDrugstoresSuppliersHeaders: (state) => state.rawDrugstoresSuppliersHeaders,
        getRawDrugstoresSuppliersSearchKeys: (state) => state.rawDrugstoresSuppliersSearchKeys,


        //Products
        getProductsData: (state) => state.productsData,
        getProductsDataTotal: (state) => state.productsDataTotal,
        getProductsDataPagesCount: (state) => state.productsDataPagesCount,
        getRawDrugstoresProductsData: (state) => state.rawDrugstoresProductsData,
        getRawDrugstoresProductsDataTotal: (state) => state.rawDrugstoresProductsDataTotal,
        getRawDrugstoresProductsPagesCount: (state) => state.rawDrugstoresProductsPagesCount,
        getRawDrugstoresProductsHeaders: (state) => state.rawDrugstoresProductsHeaders,
        getRawDrugstoresProductsSearchKeys: (state) => state.rawDrugstoresProductsSearchKeys,

        //Categories
        getCategoriesData: (state) => state.categoriesData,
        getCategoriesTotal: (state) => state.categoriesTotal,
        getCategoriesPagesCount: (state) => state.categoriesPagesCount,

        // Drugstore API keys (admin)
        getDrugstoreApiKeys: (state) => state.drugstoreApiKeys,
        getDrugstoreApiKeysPagesCount: (state) => state.drugstoreApiKeysPagesCount,
        getDrugstoreApiKeysDataTotal: (state) => state.drugstoreApiKeysDataTotal,


    },
    actions: {

        // Mutations
        //User
        setWorkingProfile(profile) {
            this.workingProfile = profile;
        },
        setCentralInstance(instance) {
            this.centralInstance = instance;
        },
        setLicensedFeatures(features) {
            this.licensedFeatures = features;
        },

        setHorizontalNavbarTitle(title) {
            this.horizontalNavbarTitle = title;
        },


        //Common
        setAllowRegistration(allowRegistration) {
            this.allowRegistration = allowRegistration;
        },
        setAppCommonSettings(settings) {
            this.settings = settings;
        },
        setCurrentDrugstore(drugstore) {
            this.currentDrugstore = drugstore;
        },
        setDrugstores(drugstores) {
            this.drugstores = drugstores;
        },
        setDrugstoreGroups(drugstoreGroups) {
            this.drugstoreGroups = drugstoreGroups;
        },
        setCurrentDrugstoreGroup(drugstoreGroup) {
            this.currentDrugstoreGroup = drugstoreGroup;
        },

        setDrugstoresNameMap(drugstoresNameMap) {
            this.drugstoresNameMap = drugstoresNameMap;
        },
        setDrugstoresPagesCount(count) {
            this.drugstoresPagesCount = count;
        },

        setDrugstoresDataTotal(count) {
            this.drugstoresDataTotal = count;
        },


        // Suppliers data
        setSuppliersData(suppliersData) {
            this.suppliersData = suppliersData;
        },
        setSuppliersDataTotal(total) {
            this.suppliersDataTotal = total;
        },
        setSuppliersDataPagesCount(pagesCount) {
            this.suppliersDataPagesCount = pagesCount;
        },

        setRawDrugstoresSuppliersData(rawDrugstoresSuppliersData) {
            this.rawDrugstoresSuppliersData = rawDrugstoresSuppliersData;
        },
        setRawDrugstoresSuppliersDataTotal(total) {
            this.rawDrugstoresSuppliersDataTotal = total;
        },
        setRawDrugstoresSuppliersPagesCount(pagesCount) {
            this.rawDrugstoresSuppliersPagesCount = pagesCount;
        },
        setRawDrugstoresSuppliersHeaders(headers) {
            this.rawDrugstoresSuppliersHeaders = headers;
        },
        setRawDrugstoresSuppliersSearchKeys(searchKeys) {
            this.rawDrugstoresSuppliersSearchKeys = searchKeys;
        },


        // Products data
        setProductsData(productsData) {
            this.productsData = productsData;
        },
        setProductsDataTotal(total) {
            this.productsDataTotal = total;
        },
        setProductsDataPagesCount(pagesCount) {
            this.productsDataPagesCount = pagesCount;
        },

        setRawDrugstoresProductsData(rawDrugstoresProductsData) {
            this.rawDrugstoresProductsData = rawDrugstoresProductsData;
        },
        setRawDrugstoresProductsDataTotal(total) {
            this.rawDrugstoresProductsDataTotal = total;
        },
        setRawDrugstoresProductsPagesCount(pagesCount) {
            this.rawDrugstoresProductsPagesCount = pagesCount;
        },
        setRawDrugstoresProductsHeaders(headers) {
            this.rawDrugstoresProductsHeaders = headers;
        },
        setRawDrugstoresProductsSearchKeys(searchKeys) {
            this.rawDrugstoresProductsSearchKeys = searchKeys;
        },

// Categories
        setCategoriesData(categories) {
            this.categoriesData = categories;
        },
        setCategoriesTotal(total) {
            this.categoriesTotal = total;
        },
        setCategoriesPagesCount(count) {
            this.categoriesPagesCount = count;
        },

        setAvailableModules(availableModules) {
            this.availableModules = availableModules;
        },
        
        // Actions
        /**
         * Gets app workig profile: SIMPLEPHARM_SCOPE_CENTRAL (or APP_CENTRAL) or SIMPLEPHARM_SCOPE_LOCAL (or APP_LOCAL)
         * @param {*}
         * @returns
         */
        httpGetWorkingProfile() {
            log.log("Sending getWorkingProfile")
            log.log(authHeader())
            return axios({
                method: "get",
                url: this.basics_endpoint + "/working-profile",
                headers: {
                    Authorization: authHeader()
                    ,
                    'authMethod': 'CUSTOM_JWT'
                },
                data: {},
            }).then((response) => {
                if (response.status === 200) {
                    var workingProfile = response.data.payload;
                    if (workingProfile === "SIMPLEPHARM_SCOPE_CENTRAL" || workingProfile === "APP_CENTRAL") {
                        this.setCentralInstance(true);
                    } else {
                        this.setCentralInstance(false);
                    }

                    apiRequestsHelper.handleResponseWithDialogs(response, false);
                    return new Promise((resolve, reject) => {
                        resolve(workingProfile);
                    })
                }

                return apiRequestsHelper.handleResponseWithDialogs(response, false);
            })
                .catch((error) => {
                    return apiRequestsHelper.catchResponseError(error, "httpGetWorkingProfile");
                });
        },
        /**
         * Gets available, licensed features
         * @returns {Promise<AxiosResponse<any>>}
         */
        httpGetLicensedFeatures() {
            log.log("Sending getLicensedFeatures")
            return axios({
                method: "get",
                url: this.basics_endpoint + "/licensed-features",
                headers: {
                    Authorization: authHeader(),
                    'authMethod': 'CUSTOM_JWT'
                },
                data: {},
            })

                .then((response) => {
                    if (response.status === 200) {
                        this.setLicensedFeatures(response.data.payload);
                    }

                    return apiRequestsHelper.handleResponseWithDialogs(response, false)
                })
                .catch((error) => {
                    return apiRequestsHelper.catchResponseError(error, "httpGetLicensedFeatures");
                });

        },

        httpGetAllowRegitrationProperty() {
            return axios({
                method: "get",
                url: "/registration-allowed",
                headers: {
                    'Content-Type': 'application/json',
                },
                data: {},
            })

                .then((response) => {
                    if (response.status === 200) {
                        this.setAllowRegistration(response.data.payload);
                    }

                    return apiRequestsHelper.handleResponseWithDialogs(response, false)
                })
                .catch((error) => {
                    apiRequestsHelper.catchResponseError(error, "httpGetAllowRegitrationProperty");
                });

        },

        // =================================================== api for drugstores ================================================
        httpGetDrugstoresPaging( payload) {
            var pageNumber = payload.currentPage;
            var filterRequestObject = payload.searchAndFilterRequest;
            var endpoint = this.drugstores_endpoint + "/pages/all?page=" + pageNumber;
            if (payload.sortingKey) {
                endpoint = endpoint + "&sortingKey=" + payload.sortingKey;
            }
            if (payload.sortingDirection) {
                endpoint = endpoint + "&sortingDirection=" + payload.sortingDirection;
            }

            return axios({
                method: "post",
                url: endpoint,
                headers: {
                    Authorization: authHeader(),
                },
                data: filterRequestObject,
            })
                .then((response) => {
                    log.log(response);
                    if (response.status === 200) {
                        this.setDrugstoresPagesCount(response.data.payload.totalPages);
                        this.setDrugstoresDataTotal(response.data.payload.totalElements);
                        this.setDrugstores(response.data.payload.content);
                    } else if (response.status === 203) {
                        log.log(response);
                    } else {
                        var errorMessage = "";
                        if (response.data.message) {
                            errorMessage = response.data.message;
                        }
                        log.log("Wystąpił błąd!: " + errorMessage);
                    }
                })
                .catch((error) => {
                    log.log("Wystąpił błąd!: " + error);
                });
        },

        httpGetDrugstoresForClientIds(payload) {

            log.log("getDrugstores API request:")
            log.log(payload)

            var clientIds = payload.drugstoresClientIds;
            var endpoint_last_part = "/all/clientIds";

            var endpoint = this.drugstores_endpoint + endpoint_last_part;

            return axios({
                method: "post",
                url: endpoint,
                headers: requiredHeaders(),
                data: clientIds,
            })
        },

        httpRemoveDrugstores(payload) {
            var drugstoresIds = payload.drugstoresIds;
            var endpoint = this.drugstores_endpoint + "/delete/all/clientId";

            return axios({
                method: "delete",
                url: endpoint,
                headers: {
                    Authorization: authHeader(),
                },
                data: drugstoresIds,
            })
                .then((response) => {
                    return apiRequestsHelper.handleResponseWithDialogs(response, true);
                })
                .catch((error) => {
                    return apiRequestsHelper.catchResponseError(error, "httpRemoveDrugstores");
                });
        },

        httpGetDrugstoresPagesCount() {
            const endpoint = this.drugstores_endpoint + "/pages/total";
            return axios
                .get(endpoint, {
                    headers: {
                        Authorization: authHeader(),
                    },
                    data: {},
                })
                .then((response) => {
                    log.log(response);
                    if (response.status === 200) {
                        this.setDrugstoresPagesCount(response.data.payload);
                    } else if (response.status === 203) {
                        log.log(response);
                    } else {
                        var errorMessage = "";
                        if (response.data.message) {
                            errorMessage = response.data.message;
                        }
                        log.log("Wystąpił błąd!: " + errorMessage);
                    }
                })
                .catch((error) => {
                    log.log("Wystąpił błąd!: " + error);
                });
        },

        httpGetDrugstoreById(payload) {
            const endpoint = this.drugstores_endpoint + "/id/" + payload.drugstoreId;
            return axios({
                method: "get",
                url: endpoint,
                headers: {
                    Authorization: authHeader(),
                },
                data: {},
            });
        },

        httpGetDrugstoreByDrugstoreClientId(payload) {
            const endpoint = this.drugstores_endpoint + "/drugstoreClientId/" + payload.drugstoreClientId;
            return axios({
                method: "get",
                url: endpoint,
                headers: {
                    Authorization: authHeader(),
                },
                data: {},
            });
        },

        httpGetDrugstoreByReference(payload) {
            const endpoint =
                this.drugstores_endpoint + "/reference/" + payload.drugstoreReference;
            return axios({
                method: "get",
                url: endpoint,
                headers: {
                    Authorization: authHeader(),
                },
                data: {},
            });
        },

        httpGetCurrentDrugstore() {
            const endpoint = this.drugstores_endpoint + "/current";
            return axios({
                method: "get",
                url: endpoint,
                headers: {
                    Authorization: authHeader(),
                },
                data: {},
            })
                .then((response) => {
                    if (response.status === 203) {
                        var errorMessage = "";
                        if (response.data.message) {
                            errorMessage = response.data.message;
                        }
                        Swal.fire("Coś jest nie tak", errorMessage, "warning");
                        log.log("Wystąpił błąd!: " + errorMessage);
                    }
                })
                .catch((error) => {
                    Swal.fire("Wystąpił błąd!", error, "error");
                    log.log("Wystąpił błąd!: " + error);
                });
        },

        httpUpdateDrugstore(payload) {
            const endpoint = this.drugstores_endpoint + "/edit/" + payload.drugstoreId;
            return axios({
                method: "put",
                url: endpoint,
                headers: {
                    Authorization: authHeader(),
                },
                data: payload.drugstoreObject,
            })
                .then((response) => {
                    return apiRequestsHelper.handleResponseWithDialogs(response, true);
                })
                .catch((error) => {
                    return apiRequestsHelper.catchResponseError(error, "httpUpdateDrugstore");
                });
        },

        httpGetDrugstoreGroupsAll() {
            const endpoint = this.drugstore_groups_endpoint + "/all";
            return axios({
                method: "get",
                url: endpoint,
                headers: requiredHeaders(),
            }).then((response) => {
                if (response.status === 200) {
                    this.setDrugstoreGroups(response.data.payload);
                }
                return apiRequestsHelper.handleResponseWithDialogs(response, false);
            }).catch((error) => {
                return apiRequestsHelper.catchResponseError(error, "httpGetDrugstoreGroupsAll");
            });
        },

        httpGetDrugstoreGroupById(payload) {
            const endpoint = this.drugstore_groups_endpoint + "/id/" + payload.groupId;
            return axios({
                method: "get",
                url: endpoint,
                headers: requiredHeaders(),
            }).then((response) => {
                if (response.status === 200) {
                    this.setCurrentDrugstoreGroup(response.data.payload);
                }
                return apiRequestsHelper.handleResponseWithDialogs(response, false);
            }).catch((error) => {
                return apiRequestsHelper.catchResponseError(error, "httpGetDrugstoreGroupById");
            });
        },

        httpAddDrugstoreGroup(payload) {
            const endpoint = this.drugstore_groups_endpoint + "/add";
            return axios({
                method: "post",
                url: endpoint,
                headers: requiredHeaders(),
                data: payload.groupObject,
            }).then((response) => {
                return apiRequestsHelper.handleResponseWithDialogs(response, true);
            }).catch((error) => {
                return apiRequestsHelper.catchResponseError(error, "httpAddDrugstoreGroup");
            });
        },

        httpUpdateDrugstoreGroup(payload) {
            const endpoint = this.drugstore_groups_endpoint + "/edit/" + payload.groupId;
            return axios({
                method: "put",
                url: endpoint,
                headers: requiredHeaders(),
                data: payload.groupObject,
            }).then((response) => {
                return apiRequestsHelper.handleResponseWithDialogs(response, true);
            }).catch((error) => {
                return apiRequestsHelper.catchResponseError(error, "httpUpdateDrugstoreGroup");
            });
        },

        httpDeleteDrugstoreGroup(payload) {
            const endpoint = this.drugstore_groups_endpoint + "/delete/" + payload.groupId;
            return axios({
                method: "delete",
                url: endpoint,
                headers: requiredHeaders(),
            }).then((response) => {
                return apiRequestsHelper.handleResponseWithDialogs(response, true);
            }).catch((error) => {
                return apiRequestsHelper.catchResponseError(error, "httpDeleteDrugstoreGroup");
            });
        },

        httpAddDrugstoresToGroup(payload) {
            const endpoint = this.drugstore_groups_endpoint + "/" + payload.groupId + "/drugstores/add";
            return axios({
                method: "post",
                url: endpoint,
                headers: requiredHeaders(),
                data: payload.drugstoreIds,
            }).then((response) => {
                return apiRequestsHelper.handleResponseWithDialogs(response, true);
            }).catch((error) => {
                return apiRequestsHelper.catchResponseError(error, "httpAddDrugstoresToGroup");
            });
        },

        httpRemoveDrugstoresFromGroup(payload) {
            const endpoint = this.drugstore_groups_endpoint + "/" + payload.groupId + "/drugstores/remove";
            return axios({
                method: "post",
                url: endpoint,
                headers: requiredHeaders(),
                data: payload.drugstoreIds,
            }).then((response) => {
                return apiRequestsHelper.handleResponseWithDialogs(response, true);
            }).catch((error) => {
                return apiRequestsHelper.catchResponseError(error, "httpRemoveDrugstoresFromGroup");
            });
        },

        httpSetGroupDrugstores(payload) {
            const endpoint = this.drugstore_groups_endpoint + "/" + payload.groupId + "/drugstores";
            return axios({
                method: "put",
                url: endpoint,
                headers: requiredHeaders(),
                data: payload.drugstoreIds,
            }).then((response) => {
                return apiRequestsHelper.handleResponseWithDialogs(response, true);
            }).catch((error) => {
                return apiRequestsHelper.catchResponseError(error, "httpSetGroupDrugstores");
            });
        },

        httpGetGroupsForDrugstore(payload) {
            const endpoint = this.drugstore_groups_endpoint + "/by-drugstore/id/" + payload.drugstoreId;
            return axios({
                method: "get",
                url: endpoint,
                headers: requiredHeaders(),
            }).then((response) => {
                return apiRequestsHelper.handleResponseWithDialogs(response, false);
            }).catch((error) => {
                return apiRequestsHelper.catchResponseError(error, "httpGetGroupsForDrugstore");
            });
        },

        httpAddDrugstore(payload) {
            const endpoint = this.drugstores_endpoint + "/add";
            log.log(endpoint + this.backendModuleName);
            log.log(payload.drugstoreObject);
            return axios({
                method: "post",
                url: endpoint,
                headers: requiredHeaders(),
                data: payload.drugstoreObject,
            }).then((response) => {
                return apiRequestsHelper.handleResponseWithDialogs(response, true);
            })
                .catch((error) => {
                    return apiRequestsHelper.catchResponseError(error, "httpAddDrugstore");
                });

        },

        httpCheckDrugstoreNumber(payload) {
            const endpoint =
                this.drugstores_endpoint +
                "/validation/drugstore_number/" +
                payload.drugstoreClientId;

            return axios({
                method: "get",
                url: endpoint,
                headers: {
                    Authorization: authHeader(),
                },
                data: {},
            });
        },

        httpCheckDrugstoreReference(payload) {
            const endpoint =
                this.drugstores_endpoint +
                "/validation/reference/" +
                payload.drugstoreReference;

            return axios({
                method: "get",
                url: endpoint,
                headers: {
                    Authorization: authHeader(),
                },
                data: {},
            });
        },

        httpGetDrugstoresNameMap() {
            this.lazyLoaderModule.setDisplayLazyLoader(true);
            this.lazyLoaderModule.setProgressBarMessage("Trwa nazw aptek...");
            const endpoint =
                this.drugstores_endpoint + "/name/map";
            return axios({
                method: "get",
                url: endpoint,
                headers: {
                    Authorization: authHeader(),
                    appToken: "abc",
                },
                data: {},
            })
                .then((response) => {
                    if (response.status === 200) {
                        this.setDrugstoresNameMap(response.data.payload);
                    }
                    return apiRequestsHelper.handleResponseSilently(response, true);
                })
                .catch((error) => {
                    return apiRequestsHelper.catchResponseError(error, "httpGetDrugstoresNameMap");
                });
        },

        httpGetDrugstoresReferenceIdMap() {
            var endpoint = this.drugstores_endpoint + "/reference_id/map";
            return axios({
                method: "get",
                url: endpoint,
                headers: {
                    Authorization: authHeader(),
                },
                data: {},
            });


        },


        httpImportDrugstoresFromCentralFtp() {
            this.lazyLoaderModule.setDisplayLazyLoader(true);
            this.lazyLoaderModule.setProgressBarMessage("Trwa import z serwera FTP centralnego...");
            const endpoint =
                this.drugstores_endpoint + "/import/central/ftp";
            return axios({
                method: "post",
                url: endpoint,
                headers: {
                    Authorization: authHeader(),
                    appToken: "abc",
                },
                data: {},
            })
                .then((response) => {
                    apiRequestsHelper.handleResponseWithDialogs(response, true);
                })
                .catch((error) => {
                    apiRequestsHelper.catchResponseError(error, "httpImportDrugstoresFromCentralFtp");
                });
        },
        httpUpdateDrugstoresBaseOnSourceData() {
            this.lazyLoaderModule.setDisplayLazyLoader(true);
            this.lazyLoaderModule.setProgressBarMessage("Trwa import z serwera FTP centralnego...");
            const endpoint =
                this.drugstores_endpoint + "/update/source-data";
            return axios({
                method: "post",
                url: endpoint,
                headers: {
                    Authorization: requiredHeaders(),
                    appToken: "abc",
                },
                data: {},
            })
                .then((response) => {
                    apiRequestsHelper.handleResponseWithDialogs(response, true);
                })
                .catch((error) => {
                    apiRequestsHelper.catchResponseError(error, "httpUpdateDrugstoresBaseOnSourceData");
                });
        },

        httpImportDrugstoresFromCentralRest() {
            this.lazyLoaderModule.setDisplayLazyLoader(true);
            this.lazyLoaderModule.setProgressBarMessage("Trwa import z API centralnego...");
            const endpoint =
                this.drugstores_endpoint + "/import/central/rest";
            return axios({
                method: "post",
                url: endpoint,
                headers: {
                    Authorization: authHeader(),
                    appToken: "abc",
                },
                data: {},
            })
                .then((response) => {
                    apiRequestsHelper.handleResponseWithDialogs(response, true);
                })
                .catch((error) => {
                    apiRequestsHelper.catchResponseError(error, "httpImportDrugstoresFromCentralRest");
                });
        },


        httpExportDrugstoresToFile() {
            this.lazyLoaderModule.setDisplayLazyLoader(true);
            this.lazyLoaderModule.setProgressBarMessage("Trwa import z API centralnego...");
            const endpoint =
                this.drugstores_endpoint + "/export";
            return axios({
                method: "post",
                url: endpoint,
                headers: {
                    Authorization: authHeader(),
                    appToken: "abc",
                },
                data: {},
            })
                .then((response) => {
                    apiRequestsHelper.handleResponseWithDialogs(response, true);
                })
                .catch((error) => {
                    apiRequestsHelper.catchResponseError(error, "httpExportDrugstoresToFile");
                });
        },

        // Suppliers
        httpGetSuppliersPaging( payload) {
            var pageNumber = payload.currentPage;
            var filterRequestObject = payload.searchAndFilterRequest;
            var endpoint = this.suppliers_endpoint + "/all?page=" + pageNumber;
            if (payload.sortingKey) {
                endpoint = endpoint + "&sortingKey=" + payload.sortingKey;
            }
            if (payload.sortingDirection) {
                endpoint = endpoint + "&sortingDirection=" + payload.sortingDirection;
            }

            return axios({
                method: "post",
                url: endpoint,
                headers: {
                    Authorization: authHeader(),
                },
                data: filterRequestObject,
            })
                .then((response) => {
                    log.log(response);
                    if (response.status === 200) {
                        this.setSuppliersData(response.data.payload.content);
                        this.setSuppliersDataTotal(response.data.payload.totalElements);
                        this.setSuppliersDataPagesCount(response.data.payload.totalPages);
                    }
                    return apiRequestsHelper.handleResponseWithDialogs(response, false);
                })
                .catch((error) => {
                    return apiRequestsHelper.catchResponseError(error, "httpGetSuppliersPaging");
                });
        },

        httpGetRawDrugstoresSuppliersDataPaging(payload) {
            var pageNumber = payload.currentPage;
            var filterRequestObject = payload.searchAndFilterRequest;
            var showOnlyUnBound = payload.showOnlyUnBound;
            var boundWithCentralGuid = payload.boundWithCentralGuid

            var endpoint_last_part = "/local/all?page=";
            var endpoint = this.suppliers_endpoint + endpoint_last_part + pageNumber;
            if (showOnlyUnBound !== null && showOnlyUnBound !== undefined) {
                endpoint = endpoint + "&showOnlyUnBound=" + showOnlyUnBound;
            }
            if (boundWithCentralGuid !== null) {
                endpoint = endpoint + "&boundWithCentralGuid=" + boundWithCentralGuid;
            }
            if (payload.sortingKey) {
                endpoint = endpoint + "&sortingKey=" + payload.sortingKey;
            }
            if (payload.sortingDirection) {
                endpoint = endpoint + "&sortingDirection=" + payload.sortingDirection;
            }

            return axios({
                method: "post",
                url: endpoint,
                headers: requiredHeaders(),
                data: filterRequestObject,
            })
                .then((response) => {
                    log.log("Response from httpGetRawDrugstoresSuppliersDataPaging:");
                    log.log(response);
                    if (response.status === 200) {
                        this.setRawDrugstoresSuppliersPagesCount(response.data.payload.totalPages
                        );
                        this.setRawDrugstoresSuppliersDataTotal(response.data.payload.totalElements
                        );
                        this.setRawDrugstoresSuppliersData(response.data.payload.content);
                    }
                    return apiRequestsHelper.handleResponseWithDialogs(response, false);
                })
                .catch((error) => {
                    return apiRequestsHelper.catchResponseError(error, "httpGetRawDrugstoresSuppliersDataPaging");
                });

        },


        httpGetRawDrugstoresSuppliersHeaders() {
            var endpoint = this.suppliers_endpoint + "/headers";
            return axios({
                method: "get",
                url: endpoint,
                headers: {
                    Authorization: authHeader(),
                    'authMethod': 'CUSTOM_JWT'
                },
                data: {},
            }).then((response) => {
                if (response.status === 200) {

                    log.log(response.data.payload);
                    this.setRawDrugstoresSuppliersHeaders(response.data.payload);
                }
                return apiRequestsHelper.handleResponseWithDialogs(response, false);
            })
                .catch((error) => {
                    return apiRequestsHelper.catchResponseError(error, "httpGetRawDrugstoresSuppliersHeaders");
                });
        },

        httpGetRawDrugstoresSuppliersSearchKeys() {
            var endpoint = this.suppliers_endpoint + "/search-keys";
            return axios({
                method: "get",
                url: endpoint,
                headers: {
                    Authorization: authHeader(),
                    'authMethod': 'CUSTOM_JWT'
                },
                data: {},
            }).then((response) => {
                if (response.status === 200) {

                    log.log(response.data.payload);
                    this.setRawDrugstoresSuppliersSearchKeys(response.data.payload);
                }
                return apiRequestsHelper.handleResponseWithDialogs(response, false);
            })
                .catch((error) => {
                    return apiRequestsHelper.catchResponseError(error, "httpGetRawDrugstoresSuppliersSearchKeys");
                });
        },


        httpGetSupplierByGuid(payload) {
            var endpoint = this.suppliers_endpoint + "/guid/" + payload.guid;
            return axios({
                method: "get",
                url: endpoint,
                headers: {
                    Authorization: authHeader(),
                },
                data: {},
            }).then((response) => {
                if (response.status === 200) {
                    return new Promise((resolve) => {
                        resolve(response)
                    })
                }
                return apiRequestsHelper.handleResponseWithDialogs(response, false);

            }).catch((error) => {
                return apiRequestsHelper.catchResponseError(error, "httpGetSupplierByGuid")
            });


        },

        httpGetSupplierByNip(payload) {
            const endpoint = this.suppliers_endpoint + "/nip/" + payload.nip;
            return axios({
                method: "get",
                url: endpoint,
                headers: {
                    Authorization: authHeader(),
                },
                data: {},
            });
        },

        httpAddSupplier(payload) {
            const endpoint = this.suppliers_endpoint + "/add";
            return axios({
                method: "post",
                url: endpoint,
                headers: {
                    Authorization: authHeader(),
                },
                data: payload.supplierData,
            }).then((response) => {
                return apiRequestsHelper.handleResponseWithDialogs(response, true);
            }).catch((error) => {
                return apiRequestsHelper.catchResponseError(error, "httpAddSupplier");
            });
        },
        httpUpdateSupplierData(payload) {

            log.log("Update payload:")
            log.log(payload)
            const endpoint = this.suppliers_endpoint + "/update/" + payload.guid;
            return axios({
                method: "put",
                url: endpoint,
                headers: {
                    Authorization: authHeader(),
                },
                data: payload.supplierData,
            }).then((response) => {
                return apiRequestsHelper.handleResponseWithDialogs(response, true);
            }).catch((error) => {
                return apiRequestsHelper.catchResponseError(error, "httpUpdateSupplierData");
            });
        },

        httpDeleteSuppliersByGuids(payload) {
            const endpoint = this.suppliers_endpoint + "/delete/all";
            return axios({
                method: "post",
                url: endpoint,
                headers: {
                    Authorization: authHeader(),
                },
                data: payload.guids,
            }).then((response) => {
                return apiRequestsHelper.handleResponseWithDialogs(response, true);
            }).catch((error) => {
                return apiRequestsHelper.catchResponseError(error, "httpDeleteSuppliersByGuids");
            });
        },

        httpGetSuppliersByGuids(payload) {
            console.log("httpGetSuppliersByGuids",payload);
            const endpoint = this.suppliers_endpoint + "/all/guids";
            return axios({
                method: "post",
                url: endpoint,
                headers: {
                    Authorization: authHeader(),
                },
                data: payload.guids,
            }).then((response) => {
                if(response.status === 200) {
                    return new Promise((resolve) => {
                        resolve(response)
                    })
                }
                return apiRequestsHelper.handleResponseWithDialogs(response, false);
            }).catch((error) => {
                return apiRequestsHelper.catchResponseError(error, "httpGetSuppliersByGuids");
            });
        },

        httpUnbindLocalSuppliersIds(payload) {
            const endpoint = this.suppliers_endpoint + "/unbind";
            return axios({
                method: "post",
                url: endpoint,
                headers: {
                    Authorization: authHeader(),
                },
                data: payload.localIds,
            }).then((response) => {
                return apiRequestsHelper.handleResponseWithDialogs(response, true);
            }).catch((error) => {
                return apiRequestsHelper.catchResponseError(error, "httpUnbindLocalSuppliersIds");
            });
        },

        httpBindLocalSuppliersIds(payload) {
            var centralSupplierGuid = payload.centralSupplierGuid;
            const endpoint = this.suppliers_endpoint + "/bind/" + centralSupplierGuid;
            return axios({
                method: "post",
                url: endpoint,
                headers: {
                    Authorization: authHeader(),
                },
                data: payload.localIds,
            }).then((response) => {
                if(response.status === 200){
                    this.lazyLoaderModule.handleQueuedTaskResponse(response);
                    return new Promise((resolve) => {
                        resolve(response);
                    })
                }

                apiRequestsHelper.handleProgressResponseWithDialogs(response, false);
                this.lazyLoaderModule.handleQueuedTaskResponse(response);
                return new Promise((resolve) => {
                    resolve(response);
                })
            }).catch((error) => {
                return apiRequestsHelper.catchResponseError(error, "httpAutoBindLocalSuppliers");
            });
        },
        httpMergeCentralSuppliers(payload) {
            var targetCentralSupplierGuid = payload.targetCentralSupplierGuid;
            var sourceCentralSuppliersGuids = payload.sourceCentralSuppliersGuids;

            log.log("Merging central suppliers:")
            log.log(sourceCentralSuppliersGuids)

            const endpoint = this.suppliers_endpoint + "/merge/" + targetCentralSupplierGuid;
            return axios({
                method: "post",
                url: endpoint,
                headers: {
                    Authorization: authHeader(),
                },
                data: sourceCentralSuppliersGuids,
            });
        },

        httpCreateOrBindLocalSuppliersIds(payload) {
            const endpoint = this.suppliers_endpoint + "/create-bind";
            return axios({
                method: "post",
                url: endpoint,
                headers: {
                    Authorization: authHeader(),
                },
                data: payload.localIds,
            }).then((response) => {
                if(response.status === 200){
                    this.lazyLoaderModule.handleQueuedTaskResponse(response);
                    return new Promise((resolve) => {
                        resolve(response);
                    })
                }

                apiRequestsHelper.handleProgressResponseWithDialogs(response, false);
                this.lazyLoaderModule.handleQueuedTaskResponse(response);
                return new Promise((resolve) => {
                    resolve(response);
                })
            }).catch((error) => {
                return apiRequestsHelper.catchResponseError(error, "httpAutoBindLocalSuppliers");
            });
        },

        httpAutoBindLocalSuppliers() {
            const endpoint = this.suppliers_endpoint + "/auto-bind";
            return axios({
                method: "post",
                url: endpoint,
                headers: {
                    Authorization: authHeader(),
                },
                data: {},
            }).then((response) => {
                if(response.status === 200){
                    this.lazyLoaderModule.handleQueuedTaskResponse(response);
                    return new Promise((resolve) => {
                        resolve(response);
                    })
                }

                apiRequestsHelper.handleProgressResponseWithDialogs(response, false);
                this.lazyLoaderModule.handleQueuedTaskResponse(response);
                return new Promise((resolve) => {
                    resolve(response);
                })
            }).catch((error) => {
                return apiRequestsHelper.catchResponseError(error, "httpAutoBindLocalSuppliers");
            });
        },

        // import category from source data
        httpImportSuppliersFromSourceDrugstore(payload) {
            var endpoint = this.suppliers_endpoint + `/import/source-drugstore/${payload.drugstoreClientId}`;
            if(payload.allowUpdate){
                endpoint = endpoint + "?allowUpdate=true";
            }

            return axios({
                method: "post",
                url: endpoint,
                headers: {
                    Authorization: authHeader(),
                },
                data: {},
            }).then((response) => {
                return apiRequestsHelper.handleResponseWithDialogs(response, true);
            }).catch((error) => {
                return apiRequestsHelper.catchResponseError(error, "httpImportSuppliersFromSourceDrugstore");
            });
        },



        // Products
        httpGetProductsPaging( payload) {
            var pageNumber = payload.currentPage;
            var filterRequestObject = payload.searchAndFilterRequest;
            var endpoint = this.products_endpoint + "/all?page=" + pageNumber;
            if (payload.sortingKey) {
                endpoint = endpoint + "&sortingKey=" + payload.sortingKey;
            }
            if (payload.sortingDirection) {
                endpoint = endpoint + "&sortingDirection=" + payload.sortingDirection;
            }

            return axios({
                method: "post",
                url: endpoint,
                headers: {
                    Authorization: authHeader(),
                },
                data: filterRequestObject,
            })
                .then((response) => {
                    log.log(response);
                    if (response.status === 200) {
                        this.setProductsData(response.data.payload.content);
                        this.setProductsDataTotal(response.data.payload.totalElements);
                        this.setProductsDataPagesCount(response.data.payload.totalPages);
                    }
                    return apiRequestsHelper.handleResponseWithDialogs(response, false);
                })
                .catch((error) => {
                    return apiRequestsHelper.catchResponseError(error, "httpGetProductsPaging");
                });
        },

        httpGetRawDrugstoresProductsDataPaging(payload) {
            var pageNumber = payload.currentPage;
            var filterRequestObject = payload.searchAndFilterRequest;
            var showOnlyUnBound = payload.showOnlyUnBound;
            var boundWithCentralGuid = payload.boundWithCentralGuid

            var endpoint_last_part = "/local/all?page=";
            var endpoint = this.products_endpoint + endpoint_last_part + pageNumber;
            if (showOnlyUnBound !== null && showOnlyUnBound !== undefined) {
                endpoint = endpoint + "&showOnlyUnBound=" + showOnlyUnBound;
            }
            if (boundWithCentralGuid !== null) {
                endpoint = endpoint + "&boundWithCentralGuid=" + boundWithCentralGuid;
            }
            if (payload.sortingKey) {
                endpoint = endpoint + "&sortingKey=" + payload.sortingKey;
            }
            if (payload.sortingDirection) {
                endpoint = endpoint + "&sortingDirection=" + payload.sortingDirection;
            }

            return axios({
                method: "post",
                url: endpoint,
                headers: requiredHeaders(),
                data: filterRequestObject,
            })
                .then((response) => {
                    log.log("Response from httpGetRawDrugstoresProductsDataPaging:");
                    log.log(response);
                    if (response.status === 200) {
                        this.setRawDrugstoresProductsPagesCount(response.data.payload.totalPages
                        );
                        this.setRawDrugstoresProductsDataTotal(response.data.payload.totalElements
                        );
                        this.setRawDrugstoresProductsData(response.data.payload.content);
                    }
                    return apiRequestsHelper.handleResponseWithDialogs(response, false);
                })
                .catch((error) => {
                    return apiRequestsHelper.catchResponseError(error, "httpGetRawDrugstoresProductsDataPaging");
                });

        },


        httpGetRawDrugstoresProductsHeaders() {
            var endpoint = this.products_endpoint + "/headers";
            return axios({
                method: "get",
                url: endpoint,
                headers: {
                    Authorization: authHeader(),
                    'authMethod': 'CUSTOM_JWT'
                },
                data: {},
            }).then((response) => {
                if (response.status === 200) {

                    log.log(response.data.payload);
                    this.setRawDrugstoresProductsHeaders(response.data.payload);
                }
                return apiRequestsHelper.handleResponseWithDialogs(response, false);
            })
                .catch((error) => {
                    return apiRequestsHelper.catchResponseError(error, "httpGetRawDrugstoresProductsHeaders");
                });
        },

        httpGetRawDrugstoresProductsSearchKeys() {
            var endpoint = this.products_endpoint + "/search-keys";
            return axios({
                method: "get",
                url: endpoint,
                headers: {
                    Authorization: authHeader(),
                    'authMethod': 'CUSTOM_JWT'
                },
                data: {},
            }).then((response) => {
                if (response.status === 200) {

                    log.log(response.data.payload);
                    this.setRawDrugstoresProductsSearchKeys(response.data.payload);
                }
                return apiRequestsHelper.handleResponseWithDialogs(response, false);
            })
                .catch((error) => {
                    return apiRequestsHelper.catchResponseError(error, "httpGetRawDrugstoresProductsSearchKeys");
                });
        },


        httpGetProductByGuid(payload) {
            var endpoint = this.products_endpoint + "/guid/" + payload.guid;
            return axios({
                method: "get",
                url: endpoint,
                headers: {
                    Authorization: authHeader(),
                },
                data: {},
            });


        },
        httpGetProductsForGuidsPaging(payload) {

            var pageNumber = payload.currentPage;
            var filterRequestObject = payload.searchAndFilterRequest;

            var endpoint_last_part = "/guid/all?page=";
            var endpoint = this.products_endpoint + endpoint_last_part + pageNumber;
            if (payload.sortingKey) {
                endpoint = endpoint + "&sortingKey=" + payload.sortingKey;
            }
            if (payload.sortingDirection) {
                endpoint = endpoint + "&sortingDirection=" + payload.sortingDirection;
            }

            return axios({
                method: "post",
                url: endpoint,
                headers: {
                    Authorization: authHeader(),
                },
                data: filterRequestObject,
            });


        },

        httpAddProduct(payload) {
            const endpoint = this.products_endpoint + "/add";
            return axios({
                method: "post",
                url: endpoint,
                headers: {
                    Authorization: authHeader(),
                },
                data: payload.productData,
            }).then((response) => {
                return apiRequestsHelper.handleResponseWithDialogs(response, true);
            }).catch((error) => {
                return apiRequestsHelper.catchResponseError(error, "httpAddProduct");
            });
        },
        httpUpdateProductData(payload) {

            log.log("Update payload:")
            log.log(payload)
            const endpoint = this.products_endpoint + "/update/" + payload.guid;
            return axios({
                method: "put",
                url: endpoint,
                headers: {
                    Authorization: authHeader(),
                },
                data: payload.productData,
            }).then((response) => {
                return apiRequestsHelper.handleResponseWithDialogs(response, true);
            }).catch((error) => {
                return apiRequestsHelper.catchResponseError(error, "httpUpdateProductData");
            });
        },

        httpDeleteProductsByGuids(payload) {
            const endpoint = this.products_endpoint + "/delete/all";
            return axios({
                method: "post",
                url: endpoint,
                headers: {
                    Authorization: authHeader(),
                },
                data: payload.guids,
            }).then((response) => {
                return apiRequestsHelper.handleResponseWithDialogs(response, true);
            }).catch((error) => {
                return apiRequestsHelper.catchResponseError(error, "httpDeleteProductsByGuids");
            });
        },

        httpUnbindLocalProductsIds(payload) {
            const endpoint = this.products_endpoint + "/unbind";
            return axios({
                method: "post",
                url: endpoint,
                headers: {
                    Authorization: authHeader(),
                },
                data: payload.localIds,
            }).then((response) => {
                return apiRequestsHelper.handleResponseWithDialogs(response, true);
            }).catch((error) => {
                return apiRequestsHelper.catchResponseError(error, "httpUnbindLocalProductsIds");
            });
        },

        httpBindLocalProductsIds(payload) {
            var centralProductGuid = payload.centralProductGuid;
            const endpoint = this.products_endpoint + "/bind/" + centralProductGuid;
            return axios({
                method: "post",
                url: endpoint,
                headers: {
                    Authorization: authHeader(),
                },
                data: payload.localIds,
            }).then((response) => {
                if(response.status === 200){
                    this.lazyLoaderModule.handleQueuedTaskResponse(response);
                    return new Promise((resolve) => {
                        resolve(response);
                    })
                }

                apiRequestsHelper.handleProgressResponseWithDialogs(response, false);
                this.lazyLoaderModule.handleQueuedTaskResponse(response);
                return new Promise((resolve) => {
                    resolve(response);
                })
            }).catch((error) => {
                return apiRequestsHelper.catchResponseError(error, "httpAutoBindLocalSuppliers");
            });
        },
        httpMergeCentralProducts(payload) {
            var targetCentralProductGuid = payload.targetCentralProductGuid;
            var sourceCentralProductsGuids = payload.sourceCentralProductsGuids;

            log.log("Merging central products:")
            log.log(sourceCentralProductsGuids)

            const endpoint = this.products_endpoint + "/merge/" + targetCentralProductGuid;
            return axios({
                method: "post",
                url: endpoint,
                headers: {
                    Authorization: authHeader(),
                },
                data: sourceCentralProductsGuids,
            });
        },

        httpCreateOrBindLocalProductsIds(payload) {
            const endpoint = this.products_endpoint + "/create-bind";
            return axios({
                method: "post",
                url: endpoint,
                headers: {
                    Authorization: authHeader(),
                },
                data: payload.localIds,
            }).then((response) => {
                if(response.status === 200){
                    this.lazyLoaderModule.handleQueuedTaskResponse(response);
                    return new Promise((resolve) => {
                        resolve(response);
                    })
                }

                apiRequestsHelper.handleProgressResponseWithDialogs(response, false);
                this.lazyLoaderModule.handleQueuedTaskResponse(response);
                return new Promise((resolve) => {
                    resolve(response);
                })
            }).catch((error) => {
                return apiRequestsHelper.catchResponseError(error, "httpAutoBindLocalSuppliers");
            });
        },

        httpAutoBindLocalProducts() {
            const endpoint = this.products_endpoint + "/auto-bind";
            return axios({
                method: "post",
                url: endpoint,
                headers: {
                    Authorization: authHeader(),
                },
                data: {},
            }).then((response) => {
                if(response.status === 200){
                    this.lazyLoaderModule.handleQueuedTaskResponse(response);
                    return new Promise((resolve) => {
                        resolve(response);
                    })
                }

                apiRequestsHelper.handleProgressResponseWithDialogs(response, false);
                this.lazyLoaderModule.handleQueuedTaskResponse(response);
                return new Promise((resolve) => {
                    resolve(response);
                })
            }).catch((error) => {
                return apiRequestsHelper.catchResponseError(error, "httpAutoBindLocalProducts");
            });
        },

        // import category from source data
        httpImportProductsFromSourceDrugstore(payload) {
            var endpoint = this.products_endpoint + `/import/source-drugstore/${payload.drugstoreClientId}`;
            if(payload.allowUpdate){
                endpoint = endpoint + "?allowUpdate=true";
            }

            return axios({
                method: "post",
                url: endpoint,
                headers: {
                    Authorization: authHeader(),
                },
                data: {},
            }).then((response) => {
                return apiRequestsHelper.handleResponseWithDialogs(response, true);
            }).catch((error) => {
                return apiRequestsHelper.catchResponseError(error, "httpImportProductsFromSourceDrugstore");
            });
        },



        /// END of Products

        //  BEGIN of Categories

        //  IMPORT: Category Trees from Excel
        httpGetCategoryTreeFileKeyMap() {
            const endpoint = this.categories_endpoint + "/file-key-map";
            return axios({
                method: "get",
                url: endpoint,
                headers: {Authorization: authHeader()},
            });
        },
        httpAddCategoryTreeParsedFromFile(payload) {
            console.log("Payload to send:");
            console.log(payload)
            const endpoint = this.categories_endpoint + "/import/parsed";
            return axios({
                method: "post",
                url: endpoint,
                headers: {...authHeader(), ...requiredHeaders},
                data: payload,
            }).then((response) => {
                if(response.status === 200){
                    this.lazyLoaderModule.handleQueuedTaskResponse(response);
                    return new Promise((resolve) => {
                        resolve(response);
                    })
                }

                apiRequestsHelper.handleProgressResponseWithDialogs(response, false);
                this.lazyLoaderModule.handleQueuedTaskResponse(response);
                return new Promise((resolve) => {
                    resolve(response);
                })
            }).catch((error) => {
                return apiRequestsHelper.catchResponseError(error, "httpAddCategoryTreeParsedFromFile");
            });
        },

        // import category from source data
        httpImportCategoriesFromSourceDrugstore(payload) {
            const endpoint = this.categories_endpoint + `/import/source-drugstore/${payload.drugstoreClientId}`;
            return axios({
                method: "post",
                url: endpoint,
                headers: {
                    Authorization: authHeader(),
                },
                data: {},
            }).then((response) => {
                if(response.status === 200){
                    this.lazyLoaderModule.handleQueuedTaskResponse(response);
                    return new Promise((resolve) => {
                        resolve(response);
                    })
                }

                apiRequestsHelper.handleProgressResponseWithDialogs(response, false);
                this.lazyLoaderModule.handleQueuedTaskResponse(response);
                return new Promise((resolve) => {
                    resolve(response);
                })
            }).catch((error) => {
                return apiRequestsHelper.catchResponseError(error, "httpImportCategoriesFromSourceDrugstore");
            });
        },

        httpGetCategoriesPaging( payload) {
            var pageNumber = payload.currentPage;
            var parentCategoryGuid = payload.parentCategoryGuid;
            if (!parentCategoryGuid) {
                parentCategoryGuid = "root";
            }
            var filterRequestObject = payload.searchAndFilterRequest;
            var endpoint = this.categories_endpoint + `/parent/${parentCategoryGuid}/all?page=${pageNumber}`;
            if (payload.sortingKey) {
                endpoint = endpoint + "&sortingKey=" + payload.sortingKey;
            }
            if (payload.sortingDirection) {
                endpoint = endpoint + "&sortingDirection=" + payload.sortingDirection;
            }

            endpoint = endpoint + `&searchInSubcategories=${payload.searchInSubCategories}`;

            return axios({
                method: "post",
                url: endpoint,
                headers: {
                    Authorization: authHeader(),
                },
                data: filterRequestObject,
            })
                .then((response) => {
                    log.log("categories response");
                    log.log(response);
                    if (response.status === 200) {
                        this.setCategoriesData(response.data.payload.content);
                        this.setCategoriesTotal(response.data.payload.totalElements);
                        this.setCategoriesPagesCount(response.data.payload.totalPages);
                    }
                    return apiRequestsHelper.handleResponseWithDialogs(response, false);
                })
                .catch((error) => {
                    return apiRequestsHelper.catchResponseError(error, "httpGetCategoriesPaging");
                });
        },


        httpAddCategory(payload) {
            return axios.post(`${this.categories_endpoint}/add`, payload, {
                headers: {...authHeader(), ...requiredHeaders}
            }).then((response) => {
                log.log("categories response");
                log.log(response);
                if (response.status === 200) {
                    return new Promise((resolve) => {
                        resolve(response)
                    })
                }
                return apiRequestsHelper.handleResponseWithDialogs(response, false);
            })
                .catch((error) => {
                    return apiRequestsHelper.catchResponseError(error, "httpGetCategoriesPaging");
                });
        },

        httpUpdateCategory(payload) {
            return axios.put(`${this.categories_endpoint}/update/${payload.guid}`, payload, {
                headers: {...authHeader(), ...requiredHeaders}
            }).then((response) => {
                log.log("categories response");
                log.log(response);
                if (response.status === 200) {
                    return new Promise((resolve) => {
                        resolve(response)
                    })
                }
                return apiRequestsHelper.handleResponseWithDialogs(response, false);
            })
                .catch((error) => {
                    return apiRequestsHelper.catchResponseError(error, "httpGetCategoriesPaging");
                });
        },

        httpDeleteCategory(payload) {
            return axios.post(`${this.categories_endpoint}/delete/all`, payload.categoryGuids, {
                headers: {...authHeader(), ...requiredHeaders}
            }).then((response) => {
                if (response.status === 200) {
                    return new Promise((resolve) => {
                        resolve(response)
                    })
                }
                return apiRequestsHelper.handleResponseWithDialogs(response, false);
            })
                .catch((error) => {
                    return apiRequestsHelper.catchResponseError(error, "httpDeleteCategory");
                });
        },

        httpGetCategoryByGuid(payload) {
            return axios.get(`${this.categories_endpoint}/guid/${payload.guid}`, {
                headers: {...authHeader(), ...requiredHeaders}
            }).then((response) => {
                log.log("categories response");
                log.log(response);
                if (response.status === 200) {
                    return new Promise((resolve) => {
                        resolve(response)
                    })
                }
                return apiRequestsHelper.handleResponseWithDialogs(response, false);
            })
                .catch((error) => {
                    return apiRequestsHelper.catchResponseError(error, "httpGetCategoryByGuid");
                });
        },

        // Products in Category
        httpGetCategoryProductsPaging(payload) {
            const categoryGuid = payload.categoryGuid;
            const pageNumber = payload.currentPage ?? 1;
            const filterRequestObject = payload.searchAndFilterRequest ?? null;
            let endpoint = `${this.categories_endpoint}/guid/${categoryGuid}/all?page=${pageNumber}`;
            if (payload.sortingKey) {
                endpoint = endpoint + `&sortingKey=${payload.sortingKey}`;
            }
            if (payload.sortingDirection) {
                endpoint = endpoint + `&sortingDirection=${payload.sortingDirection}`;
            }
            return axios({
                method: "post",
                url: endpoint,
                headers: {Authorization: authHeader()},
                data: filterRequestObject,
            }).then((response) => {
                if (response.status === 200) {
                    return new Promise((resolve) => {
                        resolve(response)
                    })
                }
                return apiRequestsHelper.handleResponseWithDialogs(response, false);
            }).catch((error) => {
                return apiRequestsHelper.catchResponseError(error, "httpGetCategoryProductsPaging");
            });
        },

        httpGetProductsOfCategories(payload) {
            const categoriesGuids = payload.categoriesGuids ?? [];
            const endpoint = `${this.categories_endpoint}/products/all`;
            return axios({
                method: "post",
                url: endpoint,
                headers: {...authHeader(), ...requiredHeaders},
                data: categoriesGuids,
            }).then((response) => {
                if (response.status === 200) {
                    return new Promise((resolve) => {
                        resolve(response)
                    })
                }
                return apiRequestsHelper.handleResponseWithDialogs(response, false);
            }).catch((error) => {
                return apiRequestsHelper.catchResponseError(error, "httpGetProductsOfCategories");
            });

        },
        httpAddProductsToCategory(payload) {
            const categoryGuid = payload.categoryGuid;
            const productsGuids = payload.productsGuids ?? [];
            const endpoint = `${this.categories_endpoint}/guid/${categoryGuid}/products/add`;
            return axios({
                method: "post",
                url: endpoint,
                headers: {...authHeader(), ...requiredHeaders},
                data: productsGuids,
            }).then((response) => {
                return apiRequestsHelper.handleResponseWithDialogs(response, true);
            }).catch((error) => {
                return apiRequestsHelper.catchResponseError(error, "httpAddProductsToCategory");
            });
        },
        httpRemoveProductsFromCategory(payload) {
            const categoryGuid = payload.categoryGuid;
            const productsGuids = payload.productsGuids ?? [];
            const endpoint = `${this.categories_endpoint}/guid/${categoryGuid}/products/remove`;
            return axios({
                method: "post",
                url: endpoint,
                headers: {...authHeader(), ...requiredHeaders},
                data: productsGuids,
            }).then((response) => {
                return apiRequestsHelper.handleResponseWithDialogs(response, true);
            }).catch((error) => {
                return apiRequestsHelper.catchResponseError(error, "httpRemoveProductsFromCategory");
            });
        },


        //Settings
        saveSettings( payload) {

            var settings = payload.settings;
            var settingsInputValuesMap = payload.settingsInputValuesMap;
            var settingsToSave = [];

            for (let i = 0; i < settings.length; i++) {
                var settingInput = settingsInputValuesMap.get(settings[i].name);

                if (settings[i].settingType === "BOOLEAN") {
                    if (settingInput.checked) {
                        settings[i].value = true;
                    } else {
                        settings[i].value = false;
                    }
                    //save setting...
                    settingsToSave.push(settings[i]);
                } else if (settings[i].settingType === "DOUBLE") {
                    if (settingsHelperFunctions.valueIsNotDouble(settingInput.value)) {
                        // ignore input...
                    } else {
                        var doubleValue = parseFloat(settingInput.value.toString()).toFixed(
                            2
                        );
                        settings[i].value = doubleValue.toString();
                        //save setting...
                        settingsToSave.push(settings[i]);
                    }
                } else if (settings[i].settingType === "INTEGER") {
                    if (settingsHelperFunctions.valueIsNotInt(settingInput.value)) {
                        // ignore input...
                    } else {
                        var intValue = parseInt(settingInput.value.toString());
                        settings[i].value = intValue.toString();
                        //save setting...
                        settingsToSave.push(settings[i]);
                    }
                } else if (settings[i].settingType === "ARRAY") {
                    var settingArray = [];
                    var settingArrayParts = settingInput.value.toString().split(",");
                    var arraySettingValuePrefix = "CollectiveSetting[";
                    var arraySettingValueSuffix = "]";
                    var arraySettingValueReady = ""
                    for (let j = 0; j < settingArrayParts.length; j++) {
                        settingArray.push(settingArrayParts[j]);
                        if (j < settingArrayParts.length - 1) {
                            arraySettingValueReady = arraySettingValueReady + settingArrayParts[j] + ",";
                        } else {
                            arraySettingValueReady = arraySettingValueReady + settingArrayParts[j];
                        }
                    }
                    settings[i]["values"] = settingArray;
                    settings[i]["value"] = arraySettingValuePrefix + arraySettingValueReady + arraySettingValueSuffix;
                    //save collective setting...
                    settingsToSave.push(settings[i]);
                } else {
                    //save setting as string...
                    settings[i].value = settingInput.value.toString();

                    settingsToSave.push(settings[i]);
                }
            }

            const settingsPayload = {
                settings: settingsToSave,
            }

            return this.httpSaveAllSettings(settingsPayload);

        },

        httpSaveAllSettings(payload) {
            const settings = payload.settings;
            console.log("Saving settings:",settings);
            var endpoint = this.settings_endpoint + "/save/all";
            return axios({
                method: "post",
                url: endpoint,
                headers: {
                    Authorization: authHeader(),
                    appToken: "abc",
                },
                data: settings,
            })
                .then((response) => {
                    if(response.status === 200){
                        return new Promise((resolve) => {
                            resolve(response);
                        })
                    }
                    return apiRequestsHelper.handleResponseWithDialogs(response, false);
                })
                .catch((error) => {
                    return apiRequestsHelper.catchResponseError(error, "httpSaveAllSettings");
                });
        },

        httpSaveSimpleSetting(payload) {
            var endpoint = this.settings_endpoint + "/save";
            return axios({
                method: "post",
                url: endpoint,
                headers: {
                    Authorization: authHeader(),
                    appToken: "abc",
                },
                data: payload,
            })
                .then((response) => {
                    if (response.status === 200) {
                        log.log(response.data.payload);
                        Swal.fire("Dobra robota", "Ustawienia zapisane pomyślnie.", "success");
                        // this.setSettings", response.data.payload);
                    } else if (response.status === 203) {
                        log.log(response);
                        Swal.fire("Coś jest nie tak", response.data.message, "warning");
                    } else {
                        var errorMessage = "";
                        if (response.data.message) {
                            errorMessage = response.data.message;
                        }
                        log.log("Wystąpił nieznany błąd!: " + errorMessage);
                        Swal.fire(
                            "Wystąpił błąd",
                            response.data.message + " , " + errorMessage,
                            "warning"
                        );
                    }
                })
                .catch((error) => {
                    log.log("Wystąpił błąd!: " + error);
                });
        },

        httpSaveCollectiveSetting(payload) {
            var endpoint = this.settings_endpoint + "/save/collectiveSetting";
            return axios({
                method: "post",
                url: endpoint,
                headers: {
                    Authorization: authHeader(),
                    appToken: "abc",
                },
                data: payload,
            })
                .then((response) => {
                    if (response.status === 200) {
                        Swal.fire("Dobra robota", "Ustawienia zapisane pomyślnie.", "success");
                    } else if (response.status === 203) {
                        log.log(response);
                        Swal.fire("Coś jest nie tak", response.data.message, "warning");
                    } else {
                        var errorMessage = "";
                        if (response.data.message) {
                            errorMessage = response.data.message;
                        }
                        log.log("Wystąpił nieznany błąd!: " + errorMessage);
                        Swal.fire(
                            "Wystąpił błąd",
                            response.data.message + " , " + errorMessage,
                            "warning"
                        );
                    }
                })
                .catch((error) => {
                    log.log("Wystąpił błąd!: " + error);
                });
        },

        httpGetAppCommonSettings() {
            var endpoint = this.settings_endpoint + "/module/" + this.backendModuleName;
            return axios({
                method: "get",
                url: endpoint,
                headers: {
                    Authorization: authHeader(),
                },
                data: {},
            })
                .then((response) => {
                    if (response.status === 200) {
                        log.log("Displaying settings payload:");
                        log.log(response.data.payload);
                        this.setAppCommonSettings(response.data.payload);
                    } else if (response.status === 203) {
                        log.log(response);
                    } else {
                        var errorMessage = "";
                        if (response.data.message) {
                            errorMessage = response.data.message;
                        }
                        log.log("Wystąpił błąd!: " + errorMessage);
                    }
                })
                .catch((error) => {
                    log.log("Wystąpił błąd!: " + error);
                });
        },

        httpGetAppSettingOverridesBySettingNamePage(settingName, page, sortingKey, sortingDirection, searchRequest) {
            const endpoint = `${this.app_setting_overrides_endpoint}/pages/setting/${settingName}?page=${page}&sortingKey=${sortingKey}&sortingDirection=${sortingDirection}`;
            return axios({
                method: "post",
                url: endpoint,
                headers: { Authorization: authHeader() },
                data: searchRequest
            });
        },

        httpSaveAppSettingOverride(dto) {
            console.log("Saving app setting override:",dto);
            const endpoint = `${this.app_setting_overrides_endpoint}/save`;
            return axios({
                method: "post",
                url: endpoint,
                headers: { Authorization: authHeader() },
                data: dto
            });
        },

        httpSaveAllAppSettingOverrides(dtos) {
            const endpoint = `${this.app_setting_overrides_endpoint}/save/all`;
            return axios({
                method: "post",
                url: endpoint,
                headers: { Authorization: authHeader() },
                data: dtos
            });
        },

        httpSaveAppSettingOverrideForDrugstores(request) {
            console.log("Saving app setting overrides for drugstores",request);
            const endpoint = `${this.app_setting_overrides_endpoint}/save/drugstores`;
            return axios({
                method: "post",
                url: endpoint,
                headers: { Authorization: authHeader() },
                data: request
            });
        },

        httpTestFtpConnectionSettings(payload) {
            const moduleName = payload.moduleName
            var endpoint = `${this.settings_endpoint}/test-connection/ftp/${moduleName}`;
            return axios({
                method: "get",
                url: endpoint,
                headers: requiredHeaders(),
                data: {},
            }).then((response) => {
                return apiRequestsHelper.handleResponseWithDialogs(response, true);

            }).catch((error) => {
                return apiRequestsHelper.catchResponseError(error, "httpTestFtpConnectionSettings")
            });

        },

        httpTestMailServerConnectionSettings(payload) {
            const moduleName = payload.moduleName
            var endpoint = `${this.settings_endpoint}/test-connection/mail/${moduleName}`;
            return axios({
                method: "get",
                url: endpoint,
                headers: requiredHeaders(),
                data: {},
            }).then((response) => {
                return apiRequestsHelper.handleResponseWithDialogs(response, true);

            }).catch((error) => {
                return apiRequestsHelper.catchResponseError(error, "httpTestMailServerConnectionSettings")
            });

        },

        httpTestRestConnectionSettings(payload) {
            const moduleName = payload.moduleName
            var endpoint = `${this.settings_endpoint}/test-connection/rest/${moduleName}`;
            return axios({
                method: "get",
                url: endpoint,
                headers: requiredHeaders(),
                data: {},
            }).then((response) => {
                return apiRequestsHelper.handleResponseWithDialogs(response, true);

            }).catch((error) => {
                return apiRequestsHelper.catchResponseError(error, "httpTestRestConnectionSettings")
            });

        },


        httpDeleteAppSettingOverride(settingName, drugstoreClientId, workstationId) {
            let endpoint = `${this.app_setting_overrides_endpoint}?settingName=${settingName}&drugstoreClientId=${drugstoreClientId}`;
            if (workstationId) {
                endpoint += `&workstationId=${workstationId}`;
            }

            return axios({
                method: "delete",
                url: endpoint,
                headers: { Authorization: authHeader() },
                data:{}
            });
        },

        httpDeleteAppSettingOverridesBulk(items) {
            const endpoint = `${this.app_setting_overrides_endpoint}/bulk`;
            return axios({
                method: "delete",
                url: endpoint,
                headers: { Authorization: authHeader() },
                data: items
            });
        },



        // httpChangeAppSettingOverridesValueBulk(request) {
        //     const endpoint = `${this.app_setting_overrides_endpoint}/save/drugstores`;
        //     console.log("Request to save bulk overrides:",endpoint);
        //     return axios({
        //         method: "post",
        //         url: endpoint,
        //         headers: { Authorization: authHeader() },
        //         data: request
        //     });
        // },

        httpGetUserSettings(payload) {
            const userId = payload?.userId || "guest";
            const cachedSettings = this.userSettingsByUserId[userId];
            const mockSettings = [

            ];
            const settings = cachedSettings || mockSettings.map((setting) => ({ ...setting }));
            return Promise.resolve({ userId, settings }).then((response) => {
                if (!cachedSettings) {
                    this.userSettingsByUserId[userId] = settings;
                }
                return response;
            });
        },
        httpSaveUserSettings(payload) {
            const userId = payload?.userId || "guest";
            const settings = payload?.settings || [];
            // TODO: replace with API call using requiredHeaders() from @/helpers/authservice/requiredHeaders.
            // POST /api/settings/user/{userId}/save/all (replace all user settings with provided payload)
            return Promise.resolve({ userId, settings }).then((response) => {
                this.userSettingsByUserId[userId] = settings;
                return response;
            });
        },

        httpImportSettingsFromCentralFtp() {
            this.lazyLoaderModule.setDisplayLazyLoader(true);
            this.lazyLoaderModule.setProgressBarMessage("Trwa import z ustawień z serwera FTP centralnego...");
            const endpoint =
                this.settings_endpoint + "/import/central/ftp";
            return axios({
                method: "post",
                url: endpoint,
                headers: {
                    Authorization: authHeader(),
                    appToken: "abc",
                },
                data: {},
            })
                .then((response) => {
                    apiRequestsHelper.handleResponseWithDialogs(response, true);
                })
                .catch((error) => {
                    apiRequestsHelper.catchResponseError(error, "httpImportSettingsFromCentralFtp");
                });
        },

        httpExportAllSettingsToFile() {
            this.lazyLoaderModule.setDisplayLazyLoader(true);
            this.lazyLoaderModule.setProgressBarMessage("Trwa import z API centralnego...");
            const endpoint =
                this.settings_endpoint + "/export";
            return axios({
                method: "post",
                url: endpoint,
                headers: {
                    Authorization: authHeader(),
                    appToken: "abc",
                },
                data: {},
            })
                .then((response) => {
                    apiRequestsHelper.handleResponseWithDialogs(response, true);
                })
                .catch((error) => {
                    apiRequestsHelper.catchResponseError(error, "httpExportAllSettingsToFile");
                });
        },

        httpExportModuleSettingsToFile(payload) {
            this.lazyLoaderModule.setDisplayLazyLoader(true);
            this.lazyLoaderModule.setProgressBarMessage("Trwa import z API centralnego...");
            const endpoint =
                this.settings_endpoint + "/export/" + payload.settingsModuleName;
            return axios({
                method: "post",
                url: endpoint,
                headers: {
                    Authorization: authHeader(),
                    appToken: "abc",
                },
                data: {},
            })
                .then((response) => {
                    apiRequestsHelper.handleResponseWithDialogs(response, true);
                })
                .catch((error) => {
                    apiRequestsHelper.catchResponseError(error, "httpExportModuleSettingsToFile");
                });
        },

        httpGetAvailableModules() {
            const endpoint =
                this.settings_endpoint +
                "/available/modules"

            return axios({
                method: "get",
                url: endpoint,
                headers: {
                    Authorization: authHeader(),
                },
                data: {},
            }).then((response) => {
                if (response.status === 200) {
                    this.setAvailableModules(response.data.payload);
                }

                return apiRequestsHelper.handleResponseSilently(response, false);
            })
                .catch((error) => {
                    return apiRequestsHelper.catchResponseError(error, "httpGetAvailableModules");
                });
        },

        // =============================== APP PROCESS LOCKS (VIEW LOCKS) =====================================


        httpAcquireAppProcessLock(payload) {
            // payload: { lockKey, scope, ttlSeconds, payloadJson }
            const endpoint = this.app_process_locks_endpoint + "/acquire";
            return axios({
                method: "post",
                url: endpoint,
                headers: requiredHeadersWithClientSession(),
                data: {
                    lockKey: payload.lockKey,
                    scope: payload.scope || "VIEW",
                    ttlSeconds: payload.ttlSeconds || 120,
                    payloadJson: payload.payloadJson || "{}",
                }
            }).then((response) => {
                console.log("Acquired lock: ", response);
                if (response.status === 200) {
                   return new Promise((resolve) => {
                       resolve(response)
                   })
                }
                return apiRequestsHelper.handleResponseWithDialogs(response, false);
            }).catch((error) => {
                    return apiRequestsHelper.catchResponseError(error, "httpAcquireAppProcessLock");
                })
        },

        httpRenewAppProcessLock(payload) {
            // payload: { lockKey, ttlSeconds, payloadJson }
            const endpoint = this.app_process_locks_endpoint + "/renew";
            return axios({
                method: "post",
                url: endpoint,
                headers: requiredHeadersWithClientSession(),
                data: {
                    lockKey: payload.lockKey,
                    ttlSeconds: payload.ttlSeconds || 120,
                    payloadJson: payload.payloadJson || "{}",
                }
            }).then((response) => {
                if (response.status === 200) {
                    return new Promise((resolve) => {
                        resolve(response)
                    })
                }
                return apiRequestsHelper.handleResponseSilently(response, false);
            }).catch((error) => {
                return apiRequestsHelper.catchResponseError(error, "httpRenewAppProcessLock");
            })
        },

        httpReleaseAppProcessLock(payload) {
            // payload: { lockKey }
            const endpoint = this.app_process_locks_endpoint + "/release";
            return axios({
                method: "post",
                url: endpoint,
                headers: requiredHeadersWithClientSession(),
                data: {
                    lockKey: payload.lockKey
                }
            }).then((response) => {
                if (response.status === 200) {
                    return new Promise((resolve) => {
                        resolve(response)
                    })
                }
                return apiRequestsHelper.handleResponseSilently(response, false);
            }).catch((error) => {
                return apiRequestsHelper.catchResponseError(error, "httpReleaseAppProcessLock");
            })
        },

        httpGetActiveAppProcessLockInfo(lockKey) {
            const endpoint = this.app_process_locks_endpoint + "/active?lockKey=" + encodeURIComponent(lockKey);
            return axios({
                method: "get",
                url: endpoint,
                headers: requiredHeadersWithClientSession(),
            }).then((response) => {
                if (response.status === 200) {
                    return new Promise((resolve) => {
                        resolve(response)
                    })
                }
                return apiRequestsHelper.handleResponseWithDialogs(response, false);
            }).catch((error) => {
                return apiRequestsHelper.catchResponseError(error, "httpGetActiveAppProcessLockInfo");
            })
        },

        // =========================================== Drugstore API keys (admin) ==========================================

        setDrugstoreApiKeys(apiKeys) {
            this.drugstoreApiKeys = apiKeys;
        },
        setDrugstoreApiKeysPagesCount(count) {
            this.drugstoreApiKeysPagesCount = count;
        },

        setDrugstoreApiKeysDataTotal(count) {
            this.drugstoreApiKeysDataTotal = count;
        },

        httpGetDrugstoreApiKeysPaging(payload) {
            let pageNumber = payload.currentPage;
            let endpoint = this.drugstore_api_keys_admin_endpoint + "?page=" + pageNumber;

            if (payload.sortingKey) {
                endpoint = endpoint + "&sortingKey=" + payload.sortingKey;
            }
            if (payload.sortingDirection) {
                endpoint = endpoint + "&sortingDirection=" + payload.sortingDirection;
            }

            return axios({
                method: "get",
                url: endpoint,
                headers: {
                    Authorization: authHeader(),
                },
            }).then((response) => {
                console.log("httpGetDrugstoreApiKeysPaging response: ", response);
                if (response.status === 200) {
                    this.setDrugstoreApiKeysPagesCount(response.data.payload.totalPages);
                    this.setDrugstoreApiKeysDataTotal(response.data.payload.totalElements);
                    this.setDrugstoreApiKeys(response.data.payload.content || []);
                }
                return apiRequestsHelper.handleResponseWithDialogs(response, false);
            }).catch((error) => {
                return apiRequestsHelper.catchResponseError(error, "httpGetDrugstoreApiKeysPaging");
            });
        },

        httpGetDrugstoreApiKey(drugstoreId) {
            const endpoint = this.drugstore_api_keys_admin_endpoint + "/" + drugstoreId;
            return axios({
                method: "get",
                url: endpoint,
                headers: {
                    Authorization: authHeader(),
                },
            }).then((response) => {
                return apiRequestsHelper.handleResponseWithDialogs(response, false);
            }).catch((error) => {
                return apiRequestsHelper.catchResponseError(error, "httpGetDrugstoreApiKey");
            });
        },

        httpCreateOrReplaceDrugstoreApiKey(drugstoreId, enabled) {
            const endpoint = this.drugstore_api_keys_admin_endpoint + "/" + drugstoreId + "?enabled=" + enabled;
            return axios({
                method: "post",
                url: endpoint,
                headers: {
                    Authorization: authHeader(),
                },
            }).then((response) => {
                return apiRequestsHelper.handleResponseWithDialogs(response, false);
            }).catch((error) => {
                return apiRequestsHelper.catchResponseError(error, "httpCreateOrReplaceDrugstoreApiKey");
            });
        },

        httpRotateDrugstoreApiKey(drugstoreId) {
            const endpoint = this.drugstore_api_keys_admin_endpoint + "/" + drugstoreId + "/rotate";
            return axios({
                method: "post",
                url: endpoint,
                headers: {
                    Authorization: authHeader(),
                },
            }).then((response) => {
                return apiRequestsHelper.handleResponseWithDialogs(response, false);
            }).catch((error) => {
                return apiRequestsHelper.catchResponseError(error, "httpRotateDrugstoreApiKey");
            });
        },

        httpSetDrugstoreApiKeyEnabled(drugstoreId, enabled) {
            const endpoint = this.drugstore_api_keys_admin_endpoint + "/" + drugstoreId + "/enabled?enabled=" + enabled;
            return axios({
                method: "patch",
                url: endpoint,
                headers: {
                    Authorization: authHeader(),
                },
            }).then((response) => {
                return apiRequestsHelper.handleResponseWithDialogs(response, true);
            }).catch((error) => {
                return apiRequestsHelper.catchResponseError(error, "httpSetDrugstoreApiKeyEnabled");
            });
        },

        httpRevokeDrugstoreApiKey(drugstoreId) {
            const endpoint = this.drugstore_api_keys_admin_endpoint + "/" + drugstoreId + "/revoke";
            return axios({
                method: "post",
                url: endpoint,
                headers: {
                    Authorization: authHeader(),
                },
            }).then((response) => {
                return apiRequestsHelper.handleResponseWithDialogs(response, false);
            }).catch((error) => {
                return apiRequestsHelper.catchResponseError(error, "httpRevokeDrugstoreApiKey");
            });
        },

        /**
         * STEP 1: propose key (does NOT save in DB yet)
         * POST /admin/drugstores/api-keys/{drugstoreId}/proposal?enabled=true|false
         * returns ApiKeyProposalDto: { proposalId, drugstoreId, rawKey, enabled, expiresAt }
         */
        httpProposeDrugstoreApiKey(drugstoreId, enabled) {
            const endpoint = this.drugstore_api_keys_admin_endpoint + "/" + drugstoreId + "/proposal?enabled=" + (enabled ? "true" : "false");

            return axios({
                method: "post",
                url: endpoint,
                headers: requiredHeaders(),
                data: {},
            })
                .then((response) => response)
                .catch((error) => {
                    log.log("Wystąpił błąd!: " + error);
                    return error?.response;
                });
        },

        /**
         * STEP 2: commit proposal (saves hash in DB, rawKey is never returned again)
         * POST /admin/drugstores/api-keys/proposals/{proposalId}/commit
         */
        httpCommitDrugstoreApiKeyProposal(proposalId) {
            const endpoint = this.drugstore_api_keys_admin_endpoint + "/proposals/" + proposalId + "/commit";

            return axios({
                method: "post",
                url: endpoint,
                headers: requiredHeaders(),
                data: {},
            })
                .then((response) => response)
                .catch((error) => {
                    log.log("Wystąpił błąd!: " + error);
                    return error?.response;
                });
        },

        /**
         * CANCEL proposal (removes proposal from pending storage)
         * POST /admin/drugstores/api-keys/proposals/{proposalId}/cancel
         */
        httpCancelDrugstoreApiKeyProposal(proposalId) {
            const endpoint = this.drugstore_api_keys_admin_endpoint + "/proposals/" + proposalId + "/cancel";

            return axios({
                method: "post",
                url: endpoint,
                headers: requiredHeaders(),
                data: {},
            })
                .then((response) => response)
                .catch((error) => {
                    // cancel ma być best-effort
                    log.log("Wystąpił błąd!: " + error);
                    return error?.response;
                });
        },

        /**
         * Manual key set:
         * POST /admin/drugstores/api-keys/{drugstoreId}/manual
         * body: { rawKey, enabled }
         */
        httpSetManualDrugstoreApiKey(drugstoreId, rawKey, enabled) {
            const endpoint = this.drugstore_api_keys_admin_endpoint + "/" + drugstoreId + "/manual";
            const payload = { rawKey: rawKey, enabled: enabled };

            return axios({
                method: "post",
                url: endpoint,
                headers: requiredHeaders(),
                data: payload,
            })
                .then((response) => response)
                .catch((error) => {
                    log.log("Wystąpił błąd!: " + error);
                    return error?.response;
                });
        },

        httpPostPresenceCommands(payload) {
            return axios.post(`/admin/presence/commands`, payload, {
                headers: {...authHeader(), ...requiredHeaders}
            }).then((response) => {
                return apiRequestsHelper.handleResponseWithDialogs(response, false);
            }).catch((error) => {
                return apiRequestsHelper.catchResponseError(error, "httpPostPresenceCommands");
            });
        },


    }


});
